import './model-single-gallery.scss';
import 'photoswipe/style.css';
import PhotoSwipeLightbox from 'photoswipe/lightbox';

const SWIPE_THRESHOLD_RATIO = 0.2;
const DRAG_START_PX = 6;

const initSlider = ( root ) => {
	const viewport = root.querySelector( '.b-modelSingle__galleryViewport' );
	const track    = root.querySelector( '.b-modelSingle__galleryTrack' );
	const prev     = root.querySelector( '[data-gallery-dir="prev"]' );
	const next     = root.querySelector( '[data-gallery-dir="next"]' );

	if ( ! viewport || ! track ) {
		return null;
	}

	const slides = Array.from( track.children );
	if ( slides.length === 0 ) {
		return null;
	}

	let index          = 0;
	let pointerStartX  = null;
	let pointerStartOff = 0;
	let activePointer  = null;
	let dragged        = false;

	const slideWidth = () => slides[ 0 ].getBoundingClientRect().width;

	const visibleCount = () => {
		const w = slideWidth();
		return w > 0 ? Math.max( 1, Math.round( viewport.clientWidth / w ) ) : 1;
	};

	const maxIndex = () => Math.max( 0, slides.length - visibleCount() );
	const clamp    = ( v ) => Math.min( Math.max( v, 0 ), maxIndex() );

	const updateNav = () => {
		if ( prev ) {
			const atStart = index <= 0;
			prev.toggleAttribute( 'hidden', atStart );
			prev.setAttribute( 'aria-disabled', atStart ? 'true' : 'false' );
		}
		if ( next ) {
			const atEnd = index >= maxIndex();
			next.toggleAttribute( 'hidden', atEnd );
			next.setAttribute( 'aria-disabled', atEnd ? 'true' : 'false' );
		}
	};

	const setTranslate = ( px, animate ) => {
		track.style.transition = animate ? 'transform 0.35s ease' : 'none';
		track.style.transform  = `translate3d(${ px }px, 0, 0)`;
	};

	const apply = ( animate = true ) => {
		index = clamp( index );
		setTranslate( -index * slideWidth(), animate );
		updateNav();
	};

	const go = ( delta ) => {
		index = clamp( index + delta );
		apply( true );
	};

	if ( prev ) {
		prev.addEventListener( 'click', () => go( -1 ) );
	}
	if ( next ) {
		next.addEventListener( 'click', () => go( 1 ) );
	}

	const ro = new ResizeObserver( () => apply( false ) );
	ro.observe( viewport );

	viewport.addEventListener( 'pointerdown', ( e ) => {
		if ( e.pointerType === 'mouse' && e.button !== 0 ) {
			return;
		}
		dragged         = false;
		pointerStartX   = e.clientX;
		pointerStartOff = -index * slideWidth();
		activePointer   = e.pointerId;
		track.style.transition = 'none';
		// Capture pas na echte swipe; direct capture op pointerdown breekt synthetic click op
		// de child-knop (lightbox-open) in o.a. Chrome.
	} );

	viewport.addEventListener( 'pointermove', ( e ) => {
		if ( pointerStartX === null || e.pointerId !== activePointer ) {
			return;
		}
		const dx = e.clientX - pointerStartX;
		if ( Math.abs( dx ) <= DRAG_START_PX ) {
			return; // below threshold — treat as click, don't move track
		}
		dragged = true;
		if ( ! viewport.hasPointerCapture?.( e.pointerId ) ) {
			viewport.setPointerCapture?.( e.pointerId );
		}
		setTranslate( pointerStartOff + dx, false );
	} );

	const endPointer = ( e ) => {
		if ( pointerStartX === null || e.pointerId !== activePointer ) {
			return;
		}
		const dx = e.clientX - pointerStartX;
		if ( viewport.hasPointerCapture?.( e.pointerId ) ) {
			viewport.releasePointerCapture?.( e.pointerId );
		}
		pointerStartX = null;
		activePointer  = null;
		if ( ! dragged ) {
			return; // was a tap/click — let the click event handle it
		}
		const thresholdPx = Math.max( slideWidth() * SWIPE_THRESHOLD_RATIO, DRAG_START_PX + 1 );
		if ( dx < -thresholdPx ) {
			index += 1;
		} else if ( dx > thresholdPx ) {
			index -= 1;
		}
		apply( true );
		// Alleen synth. click blokkeren na een echte horizontale swipe, niet bij >6px ruis tijdens tikken.
		dragged = Math.abs( dx ) >= thresholdPx;
	};

	viewport.addEventListener( 'pointerup', endPointer );
	viewport.addEventListener( 'pointercancel', endPointer );

	track.querySelectorAll( 'a, button, img' ).forEach( ( el ) => {
		el.addEventListener( 'dragstart', ( e ) => e.preventDefault() );
	} );

	apply( false );

	return {
		wasDragged: () => dragged,
		clearDrag:  () => { dragged = false; },
	};
};

const initLightbox = ( root, slider ) => {
	const gallerySelector = root.getAttribute( 'data-pswp-gallery' );
	if ( ! gallerySelector ) {
		return;
	}

	const slideEls = Array.from( root.querySelectorAll( 'a[data-pswp-link]' ) );
	if ( slideEls.length === 0 ) {
		return;
	}

	const lightbox = new PhotoSwipeLightbox( {
		gallery: `[data-pswp-gallery="${ gallerySelector }"]`,
		children: 'a[data-pswp-link]',
		pswpModule: () => import( 'photoswipe' ),
		tapAction: 'close',
		bgOpacity: 0.92,
		showHideAnimationType: 'zoom',
	} );

	root.querySelectorAll( '.b-modelSingle__galleryOpen' ).forEach( ( btn ) => {
		btn.addEventListener( 'click', ( event ) => {
			if ( slider?.wasDragged() ) {
				slider.clearDrag();
				event.preventDefault();
			}
		} );
	} );

	lightbox.init();
};

export const initModelSingleGalleries = () => {
	document.querySelectorAll( '.js-modelSingleGallery' ).forEach( ( root ) => {
		const slider = initSlider( root );
		initLightbox( root, slider );
	} );
};


